import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { green, grey, yellow } from "@mui/material/colors";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode } from "react";
import { AttendanceLogEntry, Child } from "../models";
import { buildDateTimeFromInferredLocalTime } from "../utils";
import { StatusDot } from "./StatusDot";
import { TimeField } from "./TimeField";

function ChildName(props: { child: Child; log?: AttendanceLogEntry }): ReactNode {
  const { child, log } = props;
  const isDeleted = !!log?.deleted;
  const isSignedIn = !log?.timeOutCentralDateTime;
  const isAbsent = !log || isDeleted;
  let color: string = grey[500];
  if (isAbsent) {
    color = grey[500];
  } else if (isSignedIn) {
    color = green[500];
  } else {
    color = yellow[700];
  }
  const lastInitial = child.lastName.length > 0 ? child.lastName[0].toUpperCase() : "";

  return (
    <>
      <StatusDot color={color} />
      <Typography variant="body2">
        {child.firstName} {lastInitial}.
      </Typography>
    </>
  );
}

export function AttendanceLogEntryEditor(props: {
  child: Child;
  date: Dayjs;
  log?: AttendanceLogEntry;
  onChange: (childId: number, log: AttendanceLogEntry | null) => void;
}) {
  const { child, log, onChange } = props;
  const present = log !== undefined && !log.deleted;
  const dateTimeIn = log?.timeInCentralDateTime ? dayjs(log?.timeInCentralDateTime) : undefined;
  const dateTimeOut = log?.timeOutCentralDateTime ? dayjs(log?.timeOutCentralDateTime) : undefined;
  const timeIn = present ? (dateTimeIn?.format("h:mm") ?? "") : "";
  const timeOut = present ? (dateTimeOut?.format("h:mm") ?? "") : "";

  const label = { inputProps: { "aria-label": "In attendance" } };

  const handlePresentChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextPresent = event.target.checked;

    if (nextPresent) {
      if (log) {
        onChange(child.id, { ...log, deleted: false });
      } else {
        // Create a new attendance record
        const newLog: AttendanceLogEntry = {
          childId: child.id,
          deleted: false,
          id: 0,
          createdCentralDateTime: "2000-01-01",
          modifiedCentralDateTime: "2000-01-01",
          timeInCentralDateTime: undefined,
          timeOutCentralDateTime: undefined,
        };
        onChange(child.id, newLog);
      }
      return;
    }

    if (log) {
      onChange(child.id, { ...log, deleted: true, timeInCentralDateTime: undefined, timeOutCentralDateTime: undefined });
    } else {
      onChange(child.id, null);
    }
  };

  const handleStartTimeChange = (timeString: string) => {
    const timeIn = timeString.trim().length > 0 ? buildDateTimeFromInferredLocalTime(props.date, timeString) : undefined;
    onChange(child.id, getUpdatedRecord(timeIn, dateTimeOut));
  };

  const handleEndTimeChange = (timeString: string) => {
    const timeOut = timeString.trim().length > 0 ? buildDateTimeFromInferredLocalTime(props.date, timeString) : undefined;
    onChange(child.id, getUpdatedRecord(dateTimeIn, timeOut));
  };

  const getUpdatedRecord = (timeIn?: Dayjs, timeOut?: Dayjs): AttendanceLogEntry | null => {
    const isAbsent = !present;
    if (isAbsent) {
      if (!log) {
        return null;
      } else {
        return { ...log, deleted: true, timeInCentralDateTime: undefined, timeOutCentralDateTime: undefined };
      }
    } else {
      if (!log) {
        return {
          childId: child.id,
          deleted: false,
          id: 0,
          createdCentralDateTime: "2000-01-01", // doesn't matter what it is, it will be set by the api
          modifiedCentralDateTime: "2000-01-01", // doesn't matter what it is, it will be set by the api
          timeInCentralDateTime: timeIn?.isValid() ? timeIn.format("YYYY-MM-DDTHH:mm:ss") : undefined,
          timeOutCentralDateTime: timeOut?.isValid() ? timeOut.format("YYYY-MM-DDTHH:mm:ss") : undefined,
        };
      } else {
        return {
          ...log,
          deleted: false,
          timeInCentralDateTime: timeIn?.isValid() ? timeIn.format("YYYY-MM-DDTHH:mm:ss") : undefined,
          timeOutCentralDateTime: timeOut?.isValid() ? timeOut.format("YYYY-MM-DDTHH:mm:ss") : undefined,
        };
      }
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={0.5} flex={1}>
        <Checkbox {...label} checked={present} onChange={handlePresentChanged} />
        <Box
          onClick={(_e) => {
            const event = {
              target: { checked: !present },
            } as React.ChangeEvent<HTMLInputElement>;
            handlePresentChanged(event);
          }}
          sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <ChildName child={child} log={log} />
        </Box>
        <Box flexGrow={1} />
        <TimeField variant="filled" value={timeIn} sx={{ width: 95 }} disabled={!present} showNowButton onTimeChange={handleStartTimeChange} />
        &mdash;
        <TimeField variant="filled" value={timeOut} sx={{ width: 95 }} disabled={!present} showNowButton onTimeChange={handleEndTimeChange} />
      </Stack>
    </Box>
  );
}
